/**
 * ONE definition of "which Command Center does this user get" and therefore
 * "does Card Fleet Ops live inside it".
 *
 * Mirrors the backend: an organization whose vertical is not explicitly
 * hotel / property (condo) / automotive / grocery resolves to RESTAURANT,
 * which is what /api/command-center returns for it. `organization_cc_vertical`
 * is the server's own resolved value (login + /auth/me).
 *
 * SUPER ADMINS have no single vertical — they oversee all of them — so for
 * them THE ORGANIZATION SELECTED AT THE TOP OF THE COMMAND CENTER DECIDES.
 * The Command Center records the vertical it resolved for each organization
 * it has shown; this module reads that back for whichever organization is
 * selected right now. On "All organizations" (a mixed scope with no single
 * vertical) nothing is hidden — the standalone entry stays.
 */
const NON_RESTAURANT = new Set(['hotel', 'condo', 'property_management', 'automotive', 'grocery']);

export const isRestaurantVertical = (vertical) => !NON_RESTAURANT.has(vertical || '');

export const CC_VERTICAL_EVENT = 'yot:cc-vertical';

/** The ONE place the Command Center's selected organization is remembered. */
export const CC_ORG_STORAGE_KEY = 'yulontap_cc_org';

export const selectedCcOrg = () => {
    try { return localStorage.getItem(CC_ORG_STORAGE_KEY) || ''; } catch { return ''; }
};

const ccScopeKey = (userId) => `yot.cc_vertical:${userId || 'anon'}`;
const ccOrgMapKey = (userId) => `yot.cc_org_vertical:${userId || 'anon'}`;

const readOrgMap = (userId) => {
    try {
        const raw = localStorage.getItem(ccOrgMapKey(userId));
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch { return {}; }
};

/** Called by the Command Center every time it resolves a vertical for the
 *  current scope, with the organization that scope belongs to ('' / 'all'
 *  for the mixed platform scope). Notifies both navigation shells so the
 *  menu reacts without a reload. */
export const rememberCcVertical = (userId, vertical, organizationId) => {
    if (!vertical) return;
    try {
        const orgKey = organizationId || 'all';
        const map = readOrgMap(userId);
        const key = ccScopeKey(userId);
        const sameOrg = map[orgKey] === vertical;
        const sameLast = localStorage.getItem(key) === vertical;
        if (sameOrg && sameLast) return;
        map[orgKey] = vertical;
        localStorage.setItem(ccOrgMapKey(userId), JSON.stringify(map));
        localStorage.setItem(key, vertical);
        window.dispatchEvent(new Event(CC_VERTICAL_EVENT));
    } catch { /* storage unavailable — fall back to tenant vertical */ }
};

export const lastCcVertical = (userId) => {
    try { return localStorage.getItem(ccScopeKey(userId)); } catch { return null; }
};

/** The vertical of the organization currently selected in the Command
 *  Center, or null when that is unknown or the scope is "All organizations". */
export const currentCcVertical = (userId) => {
    const org = selectedCcOrg();
    if (!org || org === 'all') return null;
    return readOrgMap(userId)[org] || null;
};

/** Lets the Command Center tell both navigation shells that the selected
 *  organization changed, before the new scope's data has even landed. */
export const announceCcScopeChange = () => {
    try { window.dispatchEvent(new Event(CC_VERTICAL_EVENT)); } catch { /* no window */ }
};

/**
 * True when Card Fleet Ops should live INSIDE this user's Command Center
 * (and therefore be hidden from the side menu and the top nav, with
 * /admin/card-fleet-ops redirecting there).
 *   tenant users  → their own vertical, from the server.
 *   super admins  → the vertical of the organization SELECTED at the top of
 *                   the Command Center. "All organizations" or an
 *                   organization whose vertical is not known yet = today's
 *                   behaviour (standalone entry kept).
 */
export const fleetLivesInCommandCenter = (user) => {
    if (!user) return false;
    if (user.role === 'super_admin') {
        const scoped = currentCcVertical(user.id);
        return !!scoped && isRestaurantVertical(scoped);
    }
    return isRestaurantVertical(user.organization_cc_vertical || user.organization_vertical);
};

export const isMedicalVertical = (vertical) => (vertical || '') === 'medical';

/**
 * The vertical that applies to a page scoped to ONE organization (Review
 * Shield Results, Timeline). Resolution order, so every role works:
 *   1. the organization record itself, when the page already loaded a list
 *      (super admin),
 *   2. the tenant's own vertical from the server (/auth/me),
 *   3. the vertical the Command Center last resolved for that organization.
 * Returns null when nothing is known — callers then keep today's behaviour.
 */
export const scopedOrgVertical = ({ user, organizations = [], organizationId } = {}) => {
    const oid = organizationId || user?.organization_id || '';
    const fromList = (organizations || []).find((o) => o?.id === oid)?.vertical;
    if (fromList) return fromList;
    if (oid && oid === user?.organization_id) {
        return user?.organization_cc_vertical || user?.organization_vertical || null;
    }
    return currentCcVertical(user?.id);
};

/** Kept for readability at call sites that only care about tenants. */
export const isRestaurantTenant = fleetLivesInCommandCenter;

export default isRestaurantVertical;
